import React, { useState, useEffect } from 'react';
import { formatDate } from '../../utils/format';
import './ProcessingQueues.css';
import '../../styles/DataGrid.css';
import Card from '../common/Card';
import Table from '../common/Table';
import Pagination from '../common/Pagination';
import CorrectionModal from './CorrectionModal';
import { auth } from '../../services/api';

interface QueueItem {
  id: number;
  fileName: string;
  clientName: string;
  clientAddress?: string;
  propertyName: string;
  propertyAddress: string;
  utilityType: string;
  providerName: string;
  accountNumber: string;
  invoiceDate: string;
  dueDate?: string;
  totalAmount: number;
  confidenceScore: number;
  status: string;
  isLocked: boolean;
  lockedBy?: string;
  createdAt: string;
  s3Path: string;
}

interface ExportRun {
  taskArn: string;
  runId: string;
}

interface ExportResult {
  exitCode: number | null;
  stoppedReason: string | null;
  exports: { utilityType: string; count: number; file: string }[];
  total: number;
}

const EXPORT_POLL_MS = 10000;

// Export runs used today against the daily cap; resets at 00:00 UTC.
interface DailyUsage {
  used: number;
  cap: number;
  resetsAt: string;
}

const formatResetTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// JUMP-Infra's gold pipeline status, passed through by /api/export/pipeline.
interface PipelineStatus {
  available: boolean;
  state?: 'loading' | 'queued' | 'running' | 'idle';
  step?: number;
  steps?: number;
  label?: string;
  starts_at?: string;
  eta_at?: string;
  last_run?: { name: string; status: string; finished_at: string };
}

const PIPELINE_POLL_MS = 45000;

const formatTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

const minutesUntil = (iso?: string) =>
  iso ? Math.max(1, Math.round((new Date(iso).getTime() - Date.now()) / 60000)) : null;

interface ProcessingQueuesProps {
  initialQueue?: 'review' | 'error' | 'success';
}

const ProcessingQueues: React.FC<ProcessingQueuesProps> = ({ initialQueue = 'review' }) => {
  const [activeQueue, setActiveQueue] = useState<'review' | 'error' | 'success'>(initialQueue);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    review: 0,
    error: 0,
    success: 0
  });
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  // 'all' means no client filter. Otherwise holds the exact client_name to filter on.
  const [selectedClient, setSelectedClient] = useState<string>('all');
  // Distinct clients with rows in the processing queues — populated from the API
  // (data is server-paginated, so we can't derive this from the loaded page).
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  // True when some rows have no client — enables the "Unknown" filter option.
  const [hasUnclassified, setHasUnclassified] = useState(false);
  // "Export now": true while any exporter task (button or weekly schedule) is running.
  const [exportBusy, setExportBusy] = useState(false);
  const [exportRun, setExportRun] = useState<ExportRun | null>(null);
  const [exportMessage, setExportMessage] = useState<{ kind: 'info' | 'success' | 'error'; text: string } | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage | null>(null);
  const capReached = !!dailyUsage && dailyUsage.used >= dailyUsage.cap;
  // Gold refresh progress after an export; bumping pipelineKick forces an immediate re-read.
  const [pipeline, setPipeline] = useState<PipelineStatus | null>(null);
  const [pipelineKick, setPipelineKick] = useState(0);
  // While a refresh is still reading <Util>/input/ (running, step <= 3: about the
  // first 16 min), a new export's srcsync rebuild of input/ makes that refresh fail
  // safely and wastes one of the day's capped refreshes, so hold the button.
  const refreshReadingInputs =
    !!pipeline?.available && pipeline.state === 'running' && (pipeline.step || 0) <= 3;

  // Fetch the client filter dropdown options (scoped to the processing-queue statuses).
  const fetchClients = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/capture/clients?types=review,error,success`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const data = await response.json();
        setClientOptions(data.clients || []);
        setHasUnclassified(!!data.hasUnclassified);
      }
    } catch (error) {
      console.error('Error fetching client list:', error);
    }
  };

  // Fetch queue statistics (tab counts reflect the active client filter).
  const fetchStats = async (client: string = selectedClient) => {
    try {
      const params = new URLSearchParams();
      if (client && client !== 'all') params.set('client', client);
      const qs = params.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/capture/stats${qs ? `?${qs}` : ''}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const data = await response.json();
        setStats({
          review: data.review,
          error: data.error,
          success: data.success
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch queue items with pagination (and the active client filter).
  const fetchQueueItems = async (
    queueType: string,
    page: number = currentPage,
    rows: number = rowsPerPage,
    client: string = selectedClient
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: rows.toString()
      });
      if (client && client !== 'all') params.set('client', client);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/capture/queue/${queueType}?${params}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const data = await response.json();
        setItems(data.items);
        setTotalItems(data.totalCount);
      }
    } catch (error) {
      console.error('Error fetching queue items:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Keep tab counts in sync with the client filter.
  useEffect(() => {
    fetchStats(selectedClient);
  }, [selectedClient]);

  // Reset to first page when the queue or client filter changes.
  useEffect(() => {
    setCurrentPage(1);
    fetchQueueItems(activeQueue, 1, rowsPerPage, selectedClient);
  }, [activeQueue, selectedClient]);

  useEffect(() => {
    fetchQueueItems(activeQueue, currentPage, rowsPerPage, selectedClient);
  }, [currentPage, rowsPerPage]);

  // Whether an export is already running, so the button starts disabled if it is.
  const fetchExportStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/export/status`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const data = await response.json();
        setExportBusy(!!data.running);
        if (data.daily) setDailyUsage(data.daily);
      }
    } catch (error) {
      console.error('Error fetching export status:', error);
    }
  };

  const describeExportResult = (result: ExportResult): { kind: 'success' | 'error'; text: string } => {
    if (result.exitCode !== 0) {
      return {
        kind: 'error',
        text: `Export failed${result.stoppedReason ? `: ${result.stoppedReason}` : ''}. Utilities exported before the failure have left the queue; the rest are still here.`
      };
    }
    if (result.total === 0) {
      return { kind: 'success', text: 'Nothing to export: no file was written.' };
    }
    const parts = result.exports.map(e => `${e.utilityType} ${e.count}`).join(', ');
    return { kind: 'success', text: `${result.total} invoice${result.total === 1 ? '' : 's'} exported (${parts}).` };
  };

  const handleExportNow = async () => {
    const readyCount = stats.success;
    if (!window.confirm(`Export all ${readyCount} Ready for Export invoice${readyCount === 1 ? '' : 's'} now?`)) return;

    setExportBusy(true);
    setExportMessage({ kind: 'info', text: 'Export running… this usually takes a minute or two.' });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/export/run`, {
        method: 'POST',
        headers: { ...auth.getAuthHeaders() }
      });
      const data = await response.json();
      if (response.status === 409) {
        setExportMessage({ kind: 'info', text: 'An export is already running. Try again when it finishes.' });
        return;
      }
      if (response.status === 429) {
        setExportBusy(false);
        if (data.daily) setDailyUsage(data.daily);
        setExportMessage({
          kind: 'error',
          text: `Daily export limit reached. Ready invoices stay queued; exports resume at ${data.daily ? formatResetTime(data.daily.resetsAt) : '00:00 UTC'}.`
        });
        return;
      }
      if (!response.ok) {
        setExportBusy(false);
        setExportMessage({ kind: 'error', text: data.error || 'Failed to start export' });
        return;
      }
      setExportRun({ taskArn: data.taskArn, runId: data.runId });
    } catch (error) {
      console.error('Error starting export:', error);
      setExportBusy(false);
      setExportMessage({ kind: 'error', text: 'Failed to start export' });
    }
  };

  // Poll a button-started run until its task stops, then report and refresh the queue.
  useEffect(() => {
    if (!exportRun) return;
    const timer = setInterval(async () => {
      try {
        const params = new URLSearchParams({ taskArn: exportRun.taskArn, runId: exportRun.runId });
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/export/status?${params}`,
          { headers: { ...auth.getAuthHeaders() } }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!data.result) return;
        clearInterval(timer);
        setExportRun(null);
        setExportBusy(!!data.running);
        if (data.daily) setDailyUsage(data.daily);
        setExportMessage(describeExportResult(data.result));
        setPipelineKick(k => k + 1);
        fetchQueueItems(activeQueue);
        fetchStats();
      } catch (error) {
        console.error('Error polling export status:', error);
      }
    }, EXPORT_POLL_MS);
    return () => clearInterval(timer);
  }, [exportRun]);

  useEffect(() => {
    if (activeQueue === 'success') fetchExportStatus();
  }, [activeQueue]);

  // Read gold progress on the Ready for Export tab; keep polling only while a refresh is under way.
  useEffect(() => {
    if (activeQueue !== 'success') return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const read = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/export/pipeline`,
          { headers: { ...auth.getAuthHeaders() } }
        );
        if (!response.ok || cancelled) return;
        const data: PipelineStatus = await response.json();
        setPipeline(data);
        if (data.available && data.state && data.state !== 'idle') {
          timer = setTimeout(read, PIPELINE_POLL_MS);
        }
      } catch (error) {
        console.error('Error fetching pipeline status:', error);
      }
    };

    read();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeQueue, pipelineKick]);

  const renderPipeline = () => {
    if (!pipeline || !pipeline.available || !pipeline.state) return null;

    if (pipeline.state === 'idle') {
      const last = pipeline.last_run;
      if (!last) return null;
      const ok = last.status === 'SUCCEEDED';
      return (
        <div className={`pipeline-progress ${ok ? 'idle' : 'failed'}`} role="status">
          {ok
            ? `Dashboards up to date · last refreshed ${formatTime(last.finished_at)}`
            : `Last dashboard refresh ${last.status.toLowerCase()} at ${formatTime(last.finished_at)}`}
        </div>
      );
    }

    if (pipeline.state === 'queued') {
      // JUMP-Infra defers (never drops) a refresh past its own daily cap; its label says so.
      const deferred = /limit/i.test(pipeline.label || '');
      return (
        <div className="pipeline-progress active" role="status">
          {deferred ? 'Daily dashboard refresh limit reached' : 'Dashboard refresh queued'}
          {pipeline.starts_at ? ` · ${deferred ? 'next refresh' : 'starts'} about ${formatTime(pipeline.starts_at)}` : ''}
        </div>
      );
    }

    const steps = pipeline.steps || 0;
    const step = pipeline.step || 0;
    const left = minutesUntil(pipeline.eta_at);
    return (
      <div className="pipeline-progress active" role="status">
        <div className="pipeline-progress-text">
          Refreshing dashboards: {pipeline.label}
          <span className="pipeline-progress-meta">
            Step {step} of {steps}{left ? ` · about ${left} min left` : ''}
          </span>
        </div>
        <div className="pipeline-progress-bar" aria-hidden="true">
          {Array.from({ length: steps }, (_, i) => (
            <span key={i} className={i < step ? 'done' : ''} />
          ))}
        </div>
      </div>
    );
  };

  const handleItemClick = (item: QueueItem) => {
    setSelectedItem(item);
    setShowCorrection(true);
  };

  const handleCorrectionClose = () => {
    setShowCorrection(false);
    setSelectedItem(null);
    // Refresh the queue
    fetchQueueItems(activeQueue);
    fetchStats();
  };

  const getUtilityIcon = (type: string) => {
    const icons = {
      'Electric': '⚡',
      'Gas': '🔥',
      'Water': '💧',
      'CWS': '❄️'
    };
    return icons[type as keyof typeof icons] || '📄';
  };

  const getConfidenceBadge = (score: number) => {
    const percentage = Math.round(score * 100);
    let colorClass = 'low';
    if (percentage >= 90) colorClass = 'high';
    else if (percentage >= 70) colorClass = 'medium';
    
    return (
      <span className={`data-grid-confidence ${colorClass}`}>
        {percentage}%
      </span>
    );
  };

  const columns = [
    { key: 'clientName',    title: 'Client Name',      width: '20%' },
    { key: 'propertyName',  title: 'Property Name',    width: '20%' },
    { key: 'provider',      title: 'Utility Provider', width: '15%' },
    { key: 'accountNumber', title: 'Account Number',   width: '13%' },
    { key: 'invoiceDate',   title: 'Invoice Date',     width: '8%', align: 'center' as const },
    { key: 'dueDate',       title: 'Due Date',         width: '8%', align: 'center' as const },
    { key: 'confidence',    title: 'Confidence Score', width: '8%', align: 'center' as const },
    { key: 'action',        title: 'Action',           width: '8%', align: 'center' as const }
  ];

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  // Supplier-shape detection: use the server-provided isSupplier flag (computed in
  // capture.js via a supplier_contracts JOIN). Never derive from provider_name suffix.
  const isSupplierItem = (item: any) => !!item.isSupplier;

  const transformedData = items.map(item => ({
    clientName: (
      <div className="data-grid-info">
        <div className="data-grid-name">{item.clientName || '-'}</div>
        {item.clientAddress && <div className="data-grid-address">{item.clientAddress}</div>}
      </div>
    ),
    propertyName: (
      <div className="data-grid-info">
        {isSupplierItem(item) ? (
          <div className="data-grid-name supplier-row-label">Supplier Contract</div>
        ) : (
          <>
            <div className="data-grid-name">{item.propertyName || '-'}</div>
            {item.propertyAddress && <div className="data-grid-address">{item.propertyAddress}</div>}
          </>
        )}
      </div>
    ),
    provider: (
      <div className="data-grid-provider-cell">
        <span className="data-grid-utility-icon">{getUtilityIcon(item.utilityType)}</span>
        <span className="data-grid-provider">{item.providerName || '-'}</span>
      </div>
    ),
    accountNumber: (
      <span className="data-grid-account">{item.accountNumber || '-'}</span>
    ),
    invoiceDate: (
      <span className="data-grid-date">
        {formatDate(item.invoiceDate)}
      </span>
    ),
    dueDate: (
      <span className="data-grid-date">
        {formatDate(item.dueDate)}
      </span>
    ),
    confidence: getConfidenceBadge(item.confidenceScore),
    action: (
      <button 
        className="data-grid-action-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleItemClick(item);
        }}
        title="Edit invoice"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
    )
  }));

  return (
    <div className="processing-queues">
      <Card className="data-grid-table">
        <div className="data-grid-navigation">
          <div className="data-grid-tabs">
            <button
              className={`data-grid-tab ${activeQueue === 'review' ? 'active' : ''}`}
              onClick={() => setActiveQueue('review')}
            >
              Review <span className="count">{stats.review}</span>
            </button>
            <button
              className={`data-grid-tab ${activeQueue === 'error' ? 'active' : ''}`}
              onClick={() => setActiveQueue('error')}
            >
              Error <span className="count">{stats.error}</span>
            </button>
            <button
              className={`data-grid-tab ${activeQueue === 'success' ? 'active' : ''}`}
              onClick={() => setActiveQueue('success')}
            >
              Ready for Export <span className="count">{stats.success}</span>
            </button>
          </div>
          <div className="data-grid-actions">
            {activeQueue === 'success' && (
              <div className="export-now-group">
                <button
                  className="export-now-btn"
                  onClick={handleExportNow}
                  disabled={exportBusy || capReached || refreshReadingInputs || stats.success === 0}
                  title={
                    exportBusy ? 'An export is running'
                      : capReached ? 'Daily export limit reached'
                      : refreshReadingInputs ? 'Dashboards are loading the last export; available again in a few minutes'
                      : 'Export every Ready for Export invoice now'
                  }
                >
                  {exportBusy ? 'Exporting…' : 'Export now'}
                </button>
                {dailyUsage && (
                  <span className={`export-now-quota ${capReached ? 'exhausted' : ''}`}>
                    {capReached
                      ? `Limit reached · resets ${formatResetTime(dailyUsage.resetsAt)}`
                      : refreshReadingInputs
                        ? 'Paused while dashboards load the last export'
                        : `${dailyUsage.cap - dailyUsage.used} of ${dailyUsage.cap} left today`}
                  </span>
                )}
              </div>
            )}
            <div className="data-grid-client-filter">
              <label htmlFor="capture-processing-client-filter">Client</label>
              <select
                id="capture-processing-client-filter"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                disabled={loading}
                aria-label="Filter processing queues by client"
              >
                <option value="all">All clients</option>
                {hasUnclassified && <option value="__unknown__">Unknown</option>}
                {clientOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="data-grid-rows-control">
              <span>Show</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>
          </div>
        </div>

        {activeQueue === 'success' && exportMessage && (
          <div className={`export-now-message ${exportMessage.kind}`} role="status">
            {exportMessage.text}
          </div>
        )}

        {activeQueue === 'success' && renderPipeline()}

        <div className="data-grid-body">

          {loading ? (
            <div className="data-grid-loading">Loading queue items...</div>
          ) : items.length > 0 ? (
            <>
              <Table
                columns={columns}
                data={transformedData}
                onRowClick={(_, index) => handleItemClick(items[index])}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={setRowsPerPage}
              />
            </>
          ) : (
            <div className="data-grid-empty">
              <p>No items in {activeQueue} queue</p>
            </div>
          )}
        </div>
      </Card>

      {showCorrection && selectedItem && (
        <CorrectionModal
          invoiceId={selectedItem.id}
          utilityType={selectedItem.utilityType}
          onClose={handleCorrectionClose}
          onSave={handleCorrectionClose}
        />
      )}
    </div>
  );
};

export default ProcessingQueues;